import { supabase } from "@/integrations/supabase/client";
import { Event } from "@/types/events";
import { format } from "date-fns";

interface Host {
  id: string;
  full_name: string;
  email: string;
  phone: string;
  events_count: number;
  latest_event_status: string;
}

interface NPODashboardStats {
  acceptedEventsCount: number;
  totalEventsDone: number;
  totalParticipants: number;
  totalRevenue: number;
  acceptedEvents: Event[];
  upcomingEvents: Event[];
}

export const getNPOHosts = async (npoId: string | undefined) => {
  if (!npoId) throw new Error("NPO ID is required");

  const { data, error } = await supabase
    .from("events")
    .select(`
      host:host_profiles!events_host_id_fkey (
        id,
        full_name,
        email,
        phone
      ),
      status
    `)
    .eq("npo_id", npoId);

  if (error) throw error;

  // Transform the data to aggregate host information
  const hostsMap = data.reduce((acc: { [key: string]: Host }, event) => {
    if (!event.host) return acc;
    
    const hostId = event.host.id;
    if (!acc[hostId]) {
      acc[hostId] = {
        ...event.host,
        events_count: 1,
        latest_event_status: event.status
      };
    } else {
      acc[hostId].events_count += 1;
      // Update status only if the current event is more recent
      if (new Date(event.created_at) > new Date(acc[hostId].created_at)) {
        acc[hostId].latest_event_status = event.status;
      }
    }
    return acc;
  }, {});

  return Object.values(hostsMap);
};

export const getNPODashboardStats = async (npoId: string | undefined, token: string | undefined) => {
  try {
    const response = await fetch(
      `${import.meta.env.VITE_CC_API_URL}/v1/me/dashboard`,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to fetch dashboard data');
    }

    const { data } = await response.json();
    return {
      upcomingEvents: data.upcomingEvents || [],
      pastEvents: data.acceptedEvents || [], // Using acceptedEvents for past events
      catalogEntries: [], // API doesn't return this yet
      totalVolunteers: data.totalParticipants || 0,
      totalVolunteerHours: 0, // API doesn't return this yet
      totalEvents: data.totalEventsDone || 0,
      totalCatalogEntries: 0, // API doesn't return this yet
      totalCompanies: 0, // API doesn't return this yet
      totalRevenue: data.totalRevenue || 0,
      acceptedEventsCount: data.acceptedEventsCount || 0,
      pendingEvents: data.pendingEvents || [],
      error: null
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    return {
      upcomingEvents: [],
      pastEvents: [],
      catalogEntries: [],
      totalVolunteers: 0,
      totalVolunteerHours: 0,
      totalEvents: 0,
      totalCatalogEntries: 0,
      totalCompanies: 0,
      totalRevenue: 0,
      acceptedEventsCount: 0,
      pendingEvents: [],
      error
    };
  }
};